import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../services/api';
import Button from '../../components/ui/Button';
import LazyImage from '../../components/ui/LazyImage';
import { 
  Users, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Search, 
  Filter, 
  Mail, 
  Phone, 
  MessageSquare,
  Eye,
  FileText,
  BookOpen,
  MapPin,
  DollarSign,
  Calendar,
  AlertCircle,
  Download,
  Plus,
  X
} from 'lucide-react';

// Curriculum groupings
const MANDARIN_CURRICULUMS = [
  'cambridge_igcse_0547',
  'cambridge_igcse_0523',
  'cambridge_igcse_0509',
  'edexcel_gcse',
  'edexcel_igcse',
  'aqa_gcse',
  'edexcel_alevel',
  'cambridge_alevel',
  'ap_chinese',
  'hsk1', 'hsk2', 'hsk3', 'hsk4', 'hsk5', 'hsk6',
  'hskk_beginner', 'hskk_intermediate', 'hskk_advanced'
];

const CANTONESE_CURRICULUMS = [
  'hkdse'
];

// Curriculum categories for Mandarin
const MANDARIN_CATEGORIES = {
  proficiency: {
    label: 'Proficiency Tests',
    curriculums: ['hsk1', 'hsk2', 'hsk3', 'hsk4', 'hsk5', 'hsk6', 'hskk_beginner', 'hskk_intermediate', 'hskk_advanced']
  },
  gcse: {
    label: 'GCSE/IGCSE',
    curriculums: ['cambridge_igcse_0547', 'cambridge_igcse_0523', 'cambridge_igcse_0509', 'edexcel_gcse', 'edexcel_igcse', 'aqa_gcse']
  },
  alevel: {
    label: 'A-Level',
    curriculums: ['edexcel_alevel', 'cambridge_alevel']
  },
  ap: {
    label: 'Advanced Placement',
    curriculums: ['ap_chinese']
  }
};

// Filter configuration
const FILTER_CONFIGS = {
  status: {
    label: 'Status',
    type: 'select',
    options: [
      { value: 'pending', label: 'Pending' },
      { value: 'approved', label: 'Approved' },
      { value: 'rejected', label: 'Rejected' }
    ]
  },
  language: {
    label: 'Language',
    type: 'select',
    options: [
      { value: 'mandarin', label: 'Mandarin' },
      { value: 'cantonese', label: 'Cantonese' }
    ],
    suggestsFilters: ['curriculumCategory']
  },
  curriculumCategory: {
    label: 'Curriculum Category',
    type: 'select',
    dependsOn: 'language',
    options: (filters) => {
      const languageFilter = filters.find(f => f.type === 'language');
      if (!languageFilter) return [];
      
      if (languageFilter.value === 'mandarin') {
        return Object.entries(MANDARIN_CATEGORIES).map(([key, config]) => ({
          value: key,
          label: config.label
        }));
      }
      return []; // Cantonese only has HKDSE, no categories needed
    },
    suggestsFilters: ['specificExam']
  },
  specificExam: {
    label: 'Specific Exam',
    type: 'select',
    dependsOn: 'curriculumCategory',
    options: (filters) => {
      const categoryFilter = filters.find(f => f.type === 'curriculumCategory');
      if (!categoryFilter) return [];
      
      const category = MANDARIN_CATEGORIES[categoryFilter.value];
      if (!category) return [];
      
      // Map curriculum codes to readable labels
      const curriculumLabels = {
        'hsk1': 'HSK 1',
        'hsk2': 'HSK 2',
        'hsk3': 'HSK 3',
        'hsk4': 'HSK 4',
        'hsk5': 'HSK 5',
        'hsk6': 'HSK 6',
        'hskk_beginner': 'HSKK Beginner',
        'hskk_intermediate': 'HSKK Intermediate',
        'hskk_advanced': 'HSKK Advanced',
        'cambridge_igcse_0547': 'Cambridge IGCSE Mandarin 0547',
        'cambridge_igcse_0523': 'Cambridge IGCSE Chinese 0523',
        'cambridge_igcse_0509': 'Cambridge IGCSE Chinese 0509',
        'edexcel_gcse': 'Pearson Edexcel GCSE Chinese',
        'edexcel_igcse': 'Pearson Edexcel IGCSE Chinese',
        'aqa_gcse': 'AQA GCSE Chinese Mandarin',
        'edexcel_alevel': 'Pearson Edexcel A-Level Chinese',
        'cambridge_alevel': 'Cambridge A-Level Chinese',
        'ap_chinese': 'AP Chinese Language and Culture'
      };
      
      return category.curriculums.map(curr => ({
        value: curr,
        label: curriculumLabels[curr] || curr
      }));
    }
  },
  locationType: {
    label: 'Location Type',
    type: 'select',
    options: [
      { value: 'online_only', label: 'Online Only' },
      { value: 'in_person_only', label: 'In-Person Only' },
      { value: 'both', label: 'Both' }
    ]
  },
  dayAvailable: {
    label: 'Day Available',
    type: 'select',
    options: [
      { value: 'Monday', label: 'Monday' },
      { value: 'Tuesday', label: 'Tuesday' },
      { value: 'Wednesday', label: 'Wednesday' },
      { value: 'Thursday', label: 'Thursday' },
      { value: 'Friday', label: 'Friday' },
      { value: 'Saturday', label: 'Saturday' },
      { value: 'Sunday', label: 'Sunday' }
    ],
    suggestsFilters: ['timeAvailable']
  },
  timeAvailable: {
    label: 'Time Available',
    type: 'select',
    dependsOn: 'dayAvailable',
    options: [
      { value: '8:00 AM', label: '8:00 AM' },
      { value: '8:30 AM', label: '8:30 AM' },
      { value: '9:00 AM', label: '9:00 AM' },
      { value: '9:30 AM', label: '9:30 AM' },
      { value: '10:00 AM', label: '10:00 AM' },
      { value: '10:30 AM', label: '10:30 AM' },
      { value: '11:00 AM', label: '11:00 AM' },
      { value: '11:30 AM', label: '11:30 AM' },
      { value: '12:00 PM', label: '12:00 PM' },
      { value: '12:30 PM', label: '12:30 PM' },
      { value: '1:00 PM', label: '1:00 PM' },
      { value: '1:30 PM', label: '1:30 PM' },
      { value: '2:00 PM', label: '2:00 PM' },
      { value: '2:30 PM', label: '2:30 PM' },
      { value: '3:00 PM', label: '3:00 PM' },
      { value: '3:30 PM', label: '3:30 PM' },
      { value: '4:00 PM', label: '4:00 PM' },
      { value: '4:30 PM', label: '4:30 PM' },
      { value: '5:00 PM', label: '5:00 PM' },
      { value: '5:30 PM', label: '5:30 PM' },
      { value: '6:00 PM', label: '6:00 PM' },
      { value: '6:30 PM', label: '6:30 PM' },
      { value: '7:00 PM', label: '7:00 PM' },
      { value: '7:30 PM', label: '7:30 PM' },
      { value: '8:00 PM', label: '8:00 PM' }
    ]
  },
  teachingStyle: {
    label: 'Teaching Style',
    type: 'multiselect',
    options: [] // Dynamically populated
  },
  district: {
    label: 'District',
    type: 'multiselect',
    options: [
      { value: 'Central and Western', label: 'Central and Western' },
      { value: 'Eastern', label: 'Eastern' },
      { value: 'Southern', label: 'Southern' },
      { value: 'Wan Chai', label: 'Wan Chai' },
      { value: 'Kowloon City', label: 'Kowloon City' },
      { value: 'Kwun Tong', label: 'Kwun Tong' },
      { value: 'Sham Shui Po', label: 'Sham Shui Po' },
      { value: 'Wong Tai Sin', label: 'Wong Tai Sin' },
      { value: 'Yau Tsim Mong', label: 'Yau Tsim Mong' },
      { value: 'Islands', label: 'Islands' },
      { value: 'Kwai Tsing', label: 'Kwai Tsing' },
      { value: 'North', label: 'North' },
      { value: 'Sai Kung', label: 'Sai Kung' },
      { value: 'Sha Tin', label: 'Sha Tin' },
      { value: 'Tai Po', label: 'Tai Po' },
      { value: 'Tsuen Wan', label: 'Tsuen Wan' },
      { value: 'Tuen Mun', label: 'Tuen Mun' },
      { value: 'Yuen Long', label: 'Yuen Long' }
    ]
  },
  yearsExperience: {
    label: 'Min Years Experience',
    type: 'select',
    options: [
      { value: 1, label: '1+ years' },
      { value: 2, label: '2+ years' },
      { value: 3, label: '3+ years' },
      { value: 5, label: '5+ years' },
      { value: 10, label: '10+ years' }
    ]
  },
  offersBusinessMandarin: {
    label: 'Offers Business Mandarin',
    type: 'select',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' }
    ]
  },
  offersBusinessCantonese: {
    label: 'Offers Business Cantonese',
    type: 'select',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' }
    ]
  },
  offersSchoolInterview: {
    label: 'Offers School Interview Prep',
    type: 'select',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' }
    ]
  },
  offersJobInterview: {
    label: 'Offers Job Interview Prep',
    type: 'select',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' }
    ]
  }
};

const TutorsPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [tutors, setTutors] = useState([]);
  const [filteredTutors, setFilteredTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Filter state - default to showing only pending tutors
  const [activeFilters, setActiveFilters] = useState([
    {
      id: 'default_status_filter',
      type: 'status',
      value: 'pending'
    }
  ]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [dynamicTeachingStyles, setDynamicTeachingStyles] = useState([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [tutorsPerPage] = useState(20);

  // Availability check email blast state
  const [showEmailBlastModal, setShowEmailBlastModal] = useState(false);
  const [emailBlastLoading, setEmailBlastLoading] = useState(false);
  const [emailBlastResult, setEmailBlastResult] = useState(null);
  const [emailBlastMessage, setEmailBlastMessage] = useState('');

  // Check admin status on mount
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        const adminStatus = await api.admin.verifyAdmin(user.$id);
        if (!adminStatus) {
          navigate('/classes');
          return;
        }
        setIsAdmin(true);
      } catch (err) {
        console.error('Error checking admin status:', err);
        navigate('/classes');
      }
    };

    checkAdminStatus();
  }, [user, navigate]);

  // Load data when admin status is confirmed
  useEffect(() => {
    if (isAdmin) {
      loadTutors();
    }
  }, [isAdmin]);

  // Apply filters when tutors, filters, or search changes
  useEffect(() => {
    applyFilters();
    setCurrentPage(1); // Reset to first page when filters change
  }, [tutors, activeFilters, searchTerm]);

  // Extract unique teaching styles from tutors
  useEffect(() => {
    if (tutors.length > 0) {
      const styles = new Set();
      tutors.forEach(tutor => {
        if (tutor.teachingStyles && Array.isArray(tutor.teachingStyles)) {
          tutor.teachingStyles.forEach(style => styles.add(style));
        }
      });
      const sortedStyles = Array.from(styles).sort().map(style => ({
        value: style,
        label: style
      }));
      setDynamicTeachingStyles(sortedStyles);
    }
  }, [tutors]);

  const loadTutors = async () => {
    try {
      setLoading(true);
      setError(null);

      const tutorsData = await api.tutors.getApplications('all');
      setTutors(tutorsData);
      
    } catch (err) {
      console.error('Error loading tutors:', err);
      setError('Failed to load tutor applications');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...tutors];

    // Apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(tutor =>
        tutor.firstName?.toLowerCase().includes(search) ||
        tutor.lastName?.toLowerCase().includes(search) ||
        tutor.email?.toLowerCase().includes(search)
      );
    }

    // Apply each active filter
    activeFilters.forEach(filter => {
      filtered = applyFilter(filtered, filter);
    });

    setFilteredTutors(filtered);
  };

  const applyFilter = (tutorList, filter) => {
    switch (filter.type) {
      case 'status':
        return tutorList.filter(tutor => tutor.status === filter.value);
      
      case 'language':
        return tutorList.filter(tutor => {
          if (!tutor.curriculums || !Array.isArray(tutor.curriculums)) return false;
          
          if (filter.value === 'mandarin') {
            return tutor.curriculums.some(curr => MANDARIN_CURRICULUMS.includes(curr));
          } else if (filter.value === 'cantonese') {
            return tutor.curriculums.some(curr => CANTONESE_CURRICULUMS.includes(curr));
          }
          return true;
        });
      
      case 'curriculumCategory':
        return tutorList.filter(tutor => {
          if (!tutor.curriculums || !Array.isArray(tutor.curriculums)) return false;
          const category = MANDARIN_CATEGORIES[filter.value];
          if (!category) return false;
          return tutor.curriculums.some(curr => category.curriculums.includes(curr));
        });
      
      case 'specificExam':
        return tutorList.filter(tutor => {
          if (!tutor.curriculums || !Array.isArray(tutor.curriculums)) return false;
          return tutor.curriculums.includes(filter.value);
        });
      
      case 'locationType':
        return tutorList.filter(tutor => tutor.locationPreference === filter.value);
      
      case 'district':
        // Multiselect: filter.value is an array
        return tutorList.filter(tutor => {
          if (!tutor.baseDistricts || !Array.isArray(tutor.baseDistricts)) return false;
          if (!filter.value || filter.value.length === 0) return true;
          // Check if tutor's districts overlap with selected districts
          return filter.value.some(district => tutor.baseDistricts.includes(district));
        });
      
      case 'dayAvailable':
      case 'timeAvailable':
        return tutorList.filter(tutor => {
          if (!tutor.availability) return false;
          
          const availability = typeof tutor.availability === 'string' 
            ? JSON.parse(tutor.availability) 
            : tutor.availability;
          
          // Get day and time from active filters
          const dayFilter = filter.type === 'dayAvailable' 
            ? filter 
            : activeFilters.find(f => f.type === 'dayAvailable');
          const timeFilter = filter.type === 'timeAvailable' 
            ? filter 
            : activeFilters.find(f => f.type === 'timeAvailable');
          
          if (!dayFilter) return true; // No day filter yet
          
          if (timeFilter) {
            // Both day and time specified - format: "Monday-8:00 AM"
            const key = `${dayFilter.value}-${timeFilter.value}`;
            return availability[key] === true;
          } else {
            // Only day specified - check if available any time that day
            const dayKeys = Object.keys(availability).filter(k => 
              k.startsWith(dayFilter.value + '-') && availability[k] === true
            );
            return dayKeys.length > 0;
          }
        });
      
      case 'teachingStyle':
        // Multiselect: filter.value is an array
        return tutorList.filter(tutor => {
          if (!tutor.teachingStyles || !Array.isArray(tutor.teachingStyles)) return false;
          if (!filter.value || filter.value.length === 0) return true;
          // Check if tutor has any of the selected teaching styles
          return filter.value.some(style => tutor.teachingStyles.includes(style));
        });
      
      case 'yearsExperience':
        return tutorList.filter(tutor => {
          const years = parseInt(tutor.yearsExperience);
          return !isNaN(years) && years >= filter.value;
        });
      
      case 'offersBusinessMandarin':
        return tutorList.filter(tutor => {
          const hasDetails = tutor.businessMandarinDetails && tutor.businessMandarinDetails.trim() !== '';
          return filter.value === 'yes' ? hasDetails : !hasDetails;
        });
      
      case 'offersBusinessCantonese':
        return tutorList.filter(tutor => {
          const hasDetails = tutor.businessCantoneseDetails && tutor.businessCantoneseDetails.trim() !== '';
          return filter.value === 'yes' ? hasDetails : !hasDetails;
        });
      
      case 'offersSchoolInterview':
        return tutorList.filter(tutor => {
          const hasDetails = tutor.schoolInterviewDetails && tutor.schoolInterviewDetails.trim() !== '';
          return filter.value === 'yes' ? hasDetails : !hasDetails;
        });
      
      case 'offersJobInterview':
        return tutorList.filter(tutor => {
          const hasDetails = tutor.jobInterviewDetails && tutor.jobInterviewDetails.trim() !== '';
          return filter.value === 'yes' ? hasDetails : !hasDetails;
        });
      
      default:
        return tutorList;
    }
  };

  const addFilter = (filterType) => {
    const config = FILTER_CONFIGS[filterType];
    const newFilter = {
      id: `filter_${Date.now()}`,
      type: filterType,
      value: config.type === 'multiselect' ? [] : null
    };
    setActiveFilters([...activeFilters, newFilter]);
    setShowFilterDropdown(false);
  };

  const updateFilterValue = (filterId, value) => {
    setActiveFilters(activeFilters.map(filter =>
      filter.id === filterId ? { ...filter, value } : filter
    ));
  };

  const removeFilter = (filterId) => {
    setActiveFilters(activeFilters.filter(filter => filter.id !== filterId));
  };

  const clearAllFilters = () => {
    setActiveFilters([]);
    setSearchTerm('');
  };

  // Function to filter by status when clicking on stat cards
  const filterByStatus = (status) => {
    // Remove any existing status filter
    const withoutStatusFilter = activeFilters.filter(f => f.type !== 'status');
    
    // Add the new status filter
    const newFilter = {
      id: `status_filter_${Date.now()}`,
      type: 'status',
      value: status
    };
    
    setActiveFilters([...withoutStatusFilter, newFilter]);
  };

  const getAvailableFilterTypes = () => {
    const activeTypes = activeFilters.map(f => f.type);
    
    return Object.entries(FILTER_CONFIGS)
      .filter(([type, config]) => {
        // Don't show if already active
        if (activeTypes.includes(type)) return false;
        
        // Check dependencies
        if (config.dependsOn) {
          const dependsOnFilter = activeFilters.find(f => f.type === config.dependsOn);
          if (!dependsOnFilter || !dependsOnFilter.value) return false;
        }
        
        return true;
      })
      .map(([type, config]) => ({
        type,
        label: config.label
      }));
  };

  const getSuggestedFilters = () => {
    const suggested = new Set();
    
    activeFilters.forEach(filter => {
      const config = FILTER_CONFIGS[filter.type];
      if (config.suggestsFilters && filter.value) {
        config.suggestsFilters.forEach(type => {
          // Only suggest if not already active and dependencies are met
          const isActive = activeFilters.some(f => f.type === type);
          const depConfig = FILTER_CONFIGS[type];
          const depsMet = !depConfig.dependsOn || activeFilters.some(f => 
            f.type === depConfig.dependsOn && f.value
          );
          
          if (!isActive && depsMet) {
            suggested.add(type);
          }
        });
      }
    });
    
    return Array.from(suggested).map(type => ({
      type,
      label: FILTER_CONFIGS[type].label
    }));
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/classes');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  // Calculate stats
  const getStats = () => {
    const total = tutors.length;
    const pending = tutors.filter(t => t.status === 'pending').length;
    const approved = tutors.filter(t => t.status === 'approved').length;
    const rejected = tutors.filter(t => t.status === 'rejected').length;
    
    return { total, pending, approved, rejected };
  };

  const stats = getStats();

  // Get status display
  const getStatusDisplay = (status) => {
    switch (status) {
      case 'pending':
        return {
          color: 'text-yellow-400',
          bgColor: 'bg-yellow-500 bg-opacity-20',
          text: 'Pending Review',
          icon: <Clock size={16} />
        };
      case 'approved':
        return {
          color: 'text-green-400',
          bgColor: 'bg-green-500 bg-opacity-20',
          text: 'Approved',
          icon: <CheckCircle size={16} />
        };
      case 'rejected':
        return {
          color: 'text-red-400',
          bgColor: 'bg-red-500 bg-opacity-20',
          text: 'Rejected',
          icon: <XCircle size={16} />
        };
      default:
        return {
          color: 'text-gray-400',
          bgColor: 'bg-gray-500 bg-opacity-20',
          text: 'Unknown',
          icon: <AlertCircle size={16} />
        };
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  // Get WhatsApp link
  const getWhatsAppLink = (phone) => {
    if (!phone) return '';
    const digitsOnly = phone.replace(/\D/g, '');
    return `https://wa.me/${digitsOnly}`;
  };

  // Handle status change
  const handleStatusChange = async (tutorId, newStatus) => {
    const confirmMessage = `Are you sure you want to change the status to "${newStatus}"?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setError(null);
      const response = await api.tutors.updateStatus(tutorId, newStatus);
      
      if (response.success) {
        // Reload tutors
        await loadTutors();
      } else {
        throw new Error(response.message || 'Failed to update status');
      }
    } catch (err) {
      console.error('Error updating status:', err);
      setError(err.message || 'Failed to update status');
    }
  };

  // Get filter options
  const getFilterOptions = (filterType, filter) => {
    const config = FILTER_CONFIGS[filterType];
    
    // Handle dynamic teaching styles
    if (filterType === 'teachingStyle') {
      return dynamicTeachingStyles;
    }
    
    // Handle dependent filters
    if (typeof config.options === 'function') {
      return config.options(activeFilters);
    }
    
    return config.options || [];
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-950 text-white flex items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 text-purple-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-4 text-gray-400">Loading tutor applications...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return null; // Will redirect
  }

  const availableFilterTypes = getAvailableFilterTypes();
  const suggestedFilters = getSuggestedFilters();

  // Calculate pagination
  const indexOfLastTutor = currentPage * tutorsPerPage;
  const indexOfFirstTutor = indexOfLastTutor - tutorsPerPage;
  const currentTutors = filteredTutors.slice(indexOfFirstTutor, indexOfLastTutor);
  const totalPages = Math.ceil(filteredTutors.length / tutorsPerPage);

  // Pagination handlers
  const goToPage = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      goToPage(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 7;
    
    if (totalPages <= maxPagesToShow) {
      // Show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push('...');
      }
      
      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      
      // Always show last page
      pages.push(totalPages);
    }
    
    return pages;
  };

  const handleSendAvailabilityEmails = async () => {
    setEmailBlastLoading(true);
    setEmailBlastResult(null);
    const response = await api.tutors.sendAvailabilityCheckEmails();
    if (response.success) {
      setEmailBlastResult('success');
      setEmailBlastMessage('Availability check emails sent to all approved tutors.');
    } else {
      setEmailBlastResult('error');
      setEmailBlastMessage(response.message || 'Failed to send emails. Please try again.');
    }
    setEmailBlastLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-950 text-white">
      {/* Header */}
      <header className="bg-gray-900 border-b border-gray-800 sticky top-0 z-10 shadow-md">
        <div className="container mx-auto p-4 flex justify-between items-center">
          {/* Left side: Title + Navigation */}
          <div className="flex items-center space-x-8">
            <h1 className="text-xl font-bold">
              <span className="bg-gradient-to-r from-purple-400 to-indigo-500 text-transparent bg-clip-text">
                Admin Dashboard
              </span>
            </h1>
            
            {/* Navigation */}
            <div className="hidden sm:flex items-center space-x-6">
              <button
                onClick={() => navigate('/admin')}
                className="text-gray-300 hover:text-white transition-colors"
              >
                Classes
              </button>
              <button
                onClick={() => navigate('/admin/students')}
                className="text-gray-300 hover:text-white transition-colors"
              >
                Students
              </button>
              <span className="text-purple-400 font-medium">Tutors</span>
            </div>
          </div>
          
          {/* Right side: User controls */}
          <div className="flex items-center space-x-4">
            <span className="text-gray-300 text-sm hidden sm:inline">
              {user?.name}
            </span>
            <Button
              variant="outline"
              size="small"
              onClick={handleLogout}
              className="border-gray-600 hover:bg-gray-700"
            >
              Logout
            </Button>
          </div>
        </div>
        <div className="h-1 bg-gradient-to-r from-purple-600 via-indigo-500 to-purple-600"></div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto py-8 px-4">
        {error && (
          <div className="mb-6 p-4 bg-red-900 bg-opacity-20 border border-red-700 rounded-lg">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Page title */}
        <div className="mb-8">
          <div className="text-center">
            <div className="inline-block text-6xl font-light text-purple-400 opacity-20 mb-2">
              导师管理
            </div>
            <h2 className="text-2xl font-bold">Tutor Applications</h2>
            <div className="w-16 h-1 bg-purple-500 mx-auto mt-2"></div>
          </div>
          <div className="flex justify-end mt-4">
            <button
              onClick={() => setShowEmailBlastModal(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-purple-500 text-purple-400 hover:bg-purple-500 hover:bg-opacity-10 text-sm transition-colors"
            >
              <Mail size={15} />
              Send Availability Check Email
            </button>
          </div>
        </div>

        {/* Stats Cards - Now Clickable */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <button
            onClick={clearAllFilters}
            className="bg-gray-800 rounded-lg border border-gray-700 p-6 hover:border-purple-500 transition-colors text-left"
          >
            <div className="flex items-center">
              <Users className="h-8 w-8 text-purple-400" />
              <div className="ml-4">
                <p className="text-gray-400 text-sm">Total Applications</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </button>
        
          <button
            onClick={() => filterByStatus('pending')}
            className="bg-gray-800 rounded-lg border border-gray-700 p-6 hover:border-yellow-500 transition-colors text-left"
          >
            <div className="flex items-center">
              <Clock className="h-8 w-8 text-yellow-400" />
              <div className="ml-4">
                <p className="text-gray-400 text-sm">Pending Review</p>
                <p className="text-2xl font-bold">{stats.pending}</p>
              </div>
            </div>
          </button>
        
          <button
            onClick={() => filterByStatus('approved')}
            className="bg-gray-800 rounded-lg border border-gray-700 p-6 hover:border-green-500 transition-colors text-left"
          >
            <div className="flex items-center">
              <CheckCircle className="h-8 w-8 text-green-400" />
              <div className="ml-4">
                <p className="text-gray-400 text-sm">Approved Tutors</p>
                <p className="text-2xl font-bold">{stats.approved}</p>
              </div>
            </div>
          </button>
        
          <button
            onClick={() => filterByStatus('rejected')}
            className="bg-gray-800 rounded-lg border border-gray-700 p-6 hover:border-red-500 transition-colors text-left"
          >
            <div className="flex items-center">
              <XCircle className="h-8 w-8 text-red-400" />
              <div className="ml-4">
                <p className="text-gray-400 text-sm">Rejected</p>
                <p className="text-2xl font-bold">{stats.rejected}</p>
              </div>
            </div>
          </button>
        </div>

        {/* Smart Filter System */}
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 mb-6">
          {/* Search Bar */}
          <div className="mb-4">
            <div className="relative">
              <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>

          {/* Add Filter Button */}
          <div className="mb-4 relative">
            <button
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
              className="flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
            >
              <Plus size={16} className="mr-2" />
              Add Filter
            </button>

            {/* Filter Dropdown */}
            {showFilterDropdown && (
              <div className="absolute top-full left-0 mt-2 bg-gray-700 border border-gray-600 rounded-lg shadow-lg z-20 min-w-[200px] max-h-[400px] overflow-y-auto">
                {/* Suggested Filters */}
                {suggestedFilters.length > 0 && (
                  <>
                    <div className="px-4 py-2 text-xs text-purple-300 font-medium border-b border-gray-600">
                      Suggested
                    </div>
                    {suggestedFilters.map(({ type, label }) => (
                      <button
                        key={type}
                        onClick={() => addFilter(type)}
                        className="w-full text-left px-4 py-2 hover:bg-gray-600 text-white transition-colors flex items-center"
                      >
                        <Filter size={14} className="mr-2 text-purple-400" />
                        {label}
                      </button>
                    ))}
                    <div className="border-b border-gray-600"></div>
                  </>
                )}

                {/* All Available Filters */}
                <div className="px-4 py-2 text-xs text-gray-400 font-medium">
                  All Filters
                </div>
                {availableFilterTypes.map(({ type, label }) => (
                  <button
                    key={type}
                    onClick={() => addFilter(type)}
                    className="w-full text-left px-4 py-2 hover:bg-gray-600 text-white transition-colors"
                  >
                    {label}
                  </button>
                ))}
                
                {availableFilterTypes.length === 0 && suggestedFilters.length === 0 && (
                  <div className="px-4 py-3 text-gray-400 text-sm">
                    No more filters available
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Active Filters */}
          {activeFilters.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-gray-400">Active Filters ({activeFilters.length})</p>
                <button
                  onClick={clearAllFilters}
                  className="text-sm text-red-400 hover:text-red-300 transition-colors"
                >
                  Clear All
                </button>
              </div>
              
              <div className="flex flex-wrap gap-3">
                {activeFilters.map((filter) => {
                  const config = FILTER_CONFIGS[filter.type];
                  const options = getFilterOptions(filter.type, filter);
                  
                  return (
                    <div
                      key={filter.id}
                      className="bg-gray-700 border border-gray-600 rounded-lg p-3 flex items-center gap-3"
                    >
                      <div className="flex flex-col min-w-[150px]">
                        <label className="text-xs text-purple-300 mb-1">
                          {config.label}
                        </label>
                        {config.type === 'multiselect' ? (
                          <select
                            multiple
                            value={filter.value || []}
                            onChange={(e) => {
                              const selected = Array.from(e.target.selectedOptions, option => option.value);
                              updateFilterValue(filter.id, selected);
                            }}
                            className="bg-gray-600 border border-gray-500 rounded px-2 py-1 text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500 min-h-[100px]"
                          >
                            {options.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <select
                            value={filter.value || ''}
                            onChange={(e) => updateFilterValue(filter.id, e.target.value)}
                            className="bg-gray-600 border border-gray-500 rounded px-2 py-1 text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="">Select...</option>
                            {options.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        )}
                        {config.type === 'multiselect' && (
                          <p className="text-xs text-gray-400 mt-1">
                            Hold Ctrl/Cmd to select multiple
                          </p>
                        )}
                      </div>
                      
                      <button
                        onClick={() => removeFilter(filter.id)}
                        className="text-gray-400 hover:text-red-400 transition-colors"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Results Count */}
          <div className="mt-4 pt-4 border-t border-gray-700 text-sm text-gray-400">
            Showing <span className="text-white font-medium">{indexOfFirstTutor + 1}</span> to <span className="text-white font-medium">{Math.min(indexOfLastTutor, filteredTutors.length)}</span> of <span className="text-white font-medium">{filteredTutors.length}</span> tutors
            {totalPages > 1 && (
              <span className="ml-2">
                (Page {currentPage} of {totalPages})
              </span>
            )}
          </div>
        </div>

        {/* Tutors List */}
        <div className="space-y-4">
          {currentTutors.length === 0 ? (
            <div className="text-center py-12 bg-gray-800 rounded-lg border border-gray-700">
              <Users size={48} className="text-gray-500 mx-auto mb-4" />
              <p className="text-gray-400 text-lg mb-2">No tutor applications found</p>
              {(activeFilters.length > 0 || searchTerm) ? (
                <div>
                  <p className="text-gray-500 mb-4">Try adjusting your filters or search</p>
                  <Button
                    variant="outline"
                    onClick={clearAllFilters}
                    className="border-gray-600 hover:bg-gray-700"
                  >
                    Clear All Filters
                  </Button>
                </div>
              ) : (
                <p className="text-gray-500">No applications have been submitted yet</p>
              )}
            </div>
          ) : (
            currentTutors.map((tutor) => {
              const statusDisplay = getStatusDisplay(tutor.status);
              
              return (
                <div
                  key={tutor.$id}
                  className="bg-gray-800 rounded-lg border border-gray-700 shadow-lg p-6 hover:border-gray-600 transition-colors"
                >
                  {/* Card Header: Name, Status, and Actions */}
                  <div className="flex items-start justify-between mb-4 pb-4 border-b border-gray-700">
                    <div className="flex items-start space-x-4 flex-1">
                      {/* Profile Photo - Lazy Loaded */}
                      <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                        {tutor.profilePhotoId ? (
                          <LazyImage
                            src={`${process.env.REACT_APP_APPWRITE_ENDPOINT}/storage/buckets/tutor-files/files/${tutor.profilePhotoId}/view?project=${process.env.REACT_APP_APPWRITE_PROJECT_ID}`}
                            alt={`${tutor.firstName} ${tutor.lastName}`}
                            className="relative w-16 h-16 rounded-full overflow-hidden"
                            imgClassName="rounded-full"
                            fallback={<Users size={32} className="text-white" />}
                          />
                        ) : (
                          <Users size={32} className="text-white" />
                        )}
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-shrink-0">
                            <h3 className="text-xl font-semibold text-white mb-1">
                              {tutor.firstName} {tutor.lastName}
                            </h3>
                            <p className="text-gray-400 text-sm mb-2">
                              {tutor.yearsExperience} years experience
                            </p>
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${statusDisplay.bgColor} ${statusDisplay.color}`}>
                              {statusDisplay.icon}
                              <span className="ml-2">{statusDisplay.text}</span>
                            </span>
                          </div>
                          
                          {/* Internal Notes Callout */}
                          {tutor.internal_notes && tutor.internal_notes.trim() && (
                            <button
                              onClick={() => navigate(`/admin/tutors/${tutor.$id}`)}
                              className="flex-1 max-w-md bg-orange-900 bg-opacity-20 border border-orange-700 rounded-lg p-3 hover:bg-opacity-30 transition-colors text-left"
                            >
                              <div className="flex items-start">
                                <FileText size={16} className="text-orange-400 mr-2 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-orange-300 text-xs font-medium mb-1">Internal Notes</p>
                                  <p className="text-orange-200 text-sm line-clamp-2">
                                    {tutor.internal_notes}
                                  </p>
                                </div>
                              </div>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="flex flex-col space-y-2 ml-4">
                      <Button
                        variant="outline"
                        size="small"
                        onClick={() => navigate(`/admin/tutors/${tutor.$id}`)}
                        className="border-purple-500 text-purple-400 hover:bg-purple-500 hover:bg-opacity-10 flex items-center whitespace-nowrap"
                      >
                        <Eye size={14} className="mr-1" />
                        View Full
                      </Button>
                      
                      {/* Status buttons - always show all three */}
                      <Button
                        variant={tutor.status === 'pending' ? 'primary' : 'outline'}
                        size="small"
                        onClick={() => handleStatusChange(tutor.$id, 'pending')}
                        className={
                          tutor.status === 'pending'
                            ? 'bg-yellow-600 hover:bg-yellow-700 border-yellow-600 flex items-center whitespace-nowrap'
                            : 'border-yellow-500 text-yellow-400 hover:bg-yellow-500 hover:bg-opacity-10 flex items-center whitespace-nowrap'
                        }
                      >
                        <Clock size={14} className="mr-1" />
                        {tutor.status === 'pending' ? 'Pending' : 'Mark Pending'}
                      </Button>
                      
                      <Button
                        variant={tutor.status === 'approved' ? 'primary' : 'outline'}
                        size="small"
                        onClick={() => handleStatusChange(tutor.$id, 'approved')}
                        className={
                          tutor.status === 'approved'
                            ? 'bg-green-600 hover:bg-green-700 border-green-600 flex items-center whitespace-nowrap'
                            : 'border-green-500 text-green-400 hover:bg-green-500 hover:bg-opacity-10 flex items-center whitespace-nowrap'
                        }
                      >
                        <CheckCircle size={14} className="mr-1" />
                        {tutor.status === 'approved' ? 'Approved' : 'Approve'}
                      </Button>
                      
                      <Button
                        variant={tutor.status === 'rejected' ? 'primary' : 'outline'}
                        size="small"
                        onClick={() => handleStatusChange(tutor.$id, 'rejected')}
                        className={
                          tutor.status === 'rejected'
                            ? 'bg-red-600 hover:bg-red-700 border-red-600 flex items-center whitespace-nowrap'
                            : 'border-red-500 text-red-400 hover:bg-red-500 hover:bg-opacity-10 flex items-center whitespace-nowrap'
                        }
                      >
                        <XCircle size={14} className="mr-1" />
                        {tutor.status === 'rejected' ? 'Rejected' : 'Reject'}
                      </Button>
                    </div>
                  </div>

                  {/* Main Content Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    
                    {/* Left Column: Contact & Bio */}
                    <div>
                      <div className="mb-4">
                        <p className="flex items-center text-gray-400 text-sm mb-1">
                          <Mail size={14} className="inline mr-2" />
                          {tutor.email}
                        </p>
                      </div>

                      <div className="mb-4">
                        <p className="flex items-center text-gray-400 text-sm mb-1">
                          <Phone size={14} className="inline mr-2" />
                          {tutor.phoneNumber || 'No phone number'}
                        </p>
                      </div>

                      {tutor.tutorBio && (
                        <div className="mb-4">
                          <p className="text-purple-300 text-sm mb-1">Bio</p>
                          <p className="text-gray-300 text-sm">{tutor.tutorBio}</p>
                        </div>
                      )}

                      {/* Contact Buttons */}
                      <div className="mt-4 flex items-center space-x-2">
                        <Button
                          variant="ghost"
                          size="small"
                          onClick={() => window.open(`tel:${tutor.phoneNumber}`, '_self')}
                          className="text-green-400 hover:text-green-300 flex items-center"
                          disabled={!tutor.phoneNumber}
                        >
                          <Phone size={14} className="mr-1" />
                          Call
                        </Button>
                        <Button
                          variant="ghost"
                          size="small"
                          onClick={() => window.open(`mailto:${tutor.email}`, '_blank')}
                          className="text-blue-400 hover:text-blue-300 flex items-center"
                        >
                          <Mail size={14} className="mr-1" />
                          Email
                        </Button>
                        <Button
                          variant="ghost"
                          size="small"
                          onClick={() => window.open(getWhatsAppLink(tutor.phoneNumber), '_blank')}
                          className="text-teal-400 hover:text-teal-300 flex items-center"
                          disabled={!tutor.phoneNumber}
                        >
                          <MessageSquare size={14} className="mr-1" />
                          WhatsApp
                        </Button>
                      </div>
                    </div>

                    {/* Middle Column: Teaching Details */}
                    <div>
                      {tutor.teachingStyles && tutor.teachingStyles.length > 0 && (
                        <div className="mb-4">
                          <p className="text-purple-300 text-sm mb-1">Teaching Styles</p>
                          <div className="flex flex-wrap gap-1">
                            {tutor.teachingStyles.slice(0, 3).map((style, idx) => (
                              <span key={idx} className="text-xs bg-purple-900 bg-opacity-30 text-purple-300 px-2 py-1 rounded">
                                {style}
                              </span>
                            ))}
                            {tutor.teachingStyles.length > 3 && (
                              <span className="text-xs bg-gray-700 text-gray-400 px-2 py-1 rounded">
                                +{tutor.teachingStyles.length - 3} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {tutor.ageGroups && tutor.ageGroups.length > 0 && (
                        <div className="mb-4">
                          <p className="text-purple-300 text-sm mb-1">Age Groups</p>
                          <p className="text-gray-300 text-sm">
                            {tutor.ageGroups.length} age groups
                          </p>
                        </div>
                      )}

                      {tutor.classFormats && tutor.classFormats.length > 0 && (
                        <div className="mb-4">
                          <p className="text-purple-300 text-sm mb-1">Class Formats</p>
                          <p className="text-gray-300 text-sm">
                            {tutor.classFormats.join(', ')}
                          </p>
                        </div>
                      )}

                      {tutor.locationPreference && (
                        <div className="mb-4">
                          <p className="flex items-center text-purple-300 text-sm mb-1">
                            <MapPin size={14} className="inline mr-2" />
                            Location
                          </p>
                          <p className="text-gray-300 text-sm ml-5">
                            {tutor.locationPreference === 'online_only' && 'Online Only'}
                            {tutor.locationPreference === 'in_person_only' && 'In-Person Only'}
                            {tutor.locationPreference === 'both' && 'Online & In-Person'}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Right Column: Compensation & Documents */}
                    <div>
                      <div className="mb-4">
                        <p className="flex items-center text-purple-300 text-sm mb-1">
                          <DollarSign size={14} className="inline mr-2" />
                          Hourly Rates
                        </p>
                        <div className="ml-5 space-y-1">
                          <p className="text-gray-300 text-sm">
                            Online: HKD {tutor.hourlyRateOnline}/hr
                          </p>
                          {tutor.hourlyRateInPerson && (
                            <p className="text-gray-300 text-sm">
                              In-person: HKD {tutor.hourlyRateInPerson}/hr
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Documents */}
                      <div className="mb-4">
                        <p className="text-purple-300 text-sm mb-2">Documents</p>
                        <div className="space-y-2">
                          {tutor.resumeId && (
                            <a
                              href={`${process.env.REACT_APP_APPWRITE_ENDPOINT}/storage/buckets/tutor-files/files/${tutor.resumeId}/view?project=${process.env.REACT_APP_APPWRITE_PROJECT_ID}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center text-blue-400 hover:text-blue-300 text-sm"
                            >
                              <FileText size={14} className="mr-2" />
                              View Resume
                            </a>
                          )}
                          {tutor.sampleLessonId && (
                            <a
                              href={`${process.env.REACT_APP_APPWRITE_ENDPOINT}/storage/buckets/tutor-files/files/${tutor.sampleLessonId}/view?project=${process.env.REACT_APP_APPWRITE_PROJECT_ID}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center text-blue-400 hover:text-blue-300 text-sm"
                            >
                              <BookOpen size={14} className="mr-2" />
                              View Sample Lesson
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card Footer */}
                  <div className="mt-4 pt-4 border-t border-gray-700 flex justify-between items-center">
                    <p className="text-gray-500 text-xs">
                      Applied: {formatDate(tutor.$createdAt)}
                    </p>
                    {tutor.websiteConsent && (
                      <span className="text-xs bg-green-900 bg-opacity-30 text-green-400 px-2 py-1 rounded">
                        Website consent given
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="mt-8 flex justify-center items-center space-x-2">
            {/* Previous Button */}
            <button
              onClick={goToPrevPage}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded-lg flex items-center transition-colors ${
                currentPage === 1
                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  : 'bg-gray-800 text-white hover:bg-gray-700 border border-gray-600'
              }`}
            >
              <svg
                className="w-5 h-5 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Previous
            </button>

            {/* Page Numbers */}
            <div className="flex space-x-1">
              {getPageNumbers().map((pageNum, idx) => {
                if (pageNum === '...') {
                  return (
                    <span
                      key={`ellipsis-${idx}`}
                      className="px-4 py-2 text-gray-400"
                    >
                      ...
                    </span>
                  );
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => goToPage(pageNum)}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      currentPage === pageNum
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-600'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            {/* Next Button */}
            <button
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className={`px-4 py-2 rounded-lg flex items-center transition-colors ${
                currentPage === totalPages
                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  : 'bg-gray-800 text-white hover:bg-gray-700 border border-gray-600'
              }`}
            >
              Next
              <svg
                className="w-5 h-5 ml-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        )}
      </main>

      {/* Email blast confirmation modal */}
      {showEmailBlastModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 px-4">
          <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-bold text-white mb-2">Send Availability Check Emails</h3>
            <p className="text-gray-400 text-sm mb-6">
              This will email all <strong className="text-white">approved tutors</strong> asking
              them to review and update their availability. Each email will contain a personalised
              link. Continue?
            </p>

            {emailBlastResult === 'success' && (
              <div className="mb-4 p-3 bg-green-900 bg-opacity-30 border border-green-700 rounded-lg text-green-400 text-sm">
                {emailBlastMessage}
              </div>
            )}
            {emailBlastResult === 'error' && (
              <div className="mb-4 p-3 bg-red-900 bg-opacity-30 border border-red-700 rounded-lg text-red-400 text-sm">
                {emailBlastMessage}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowEmailBlastModal(false); setEmailBlastResult(null); }}
                disabled={emailBlastLoading}
                className="px-4 py-2 rounded-lg border border-gray-600 text-gray-300 text-sm hover:border-gray-400 disabled:opacity-50 transition-colors"
              >
                {emailBlastResult === 'success' ? 'Close' : 'Cancel'}
              </button>
              {emailBlastResult !== 'success' && (
                <button
                  onClick={handleSendAvailabilityEmails}
                  disabled={emailBlastLoading}
                  className="px-4 py-2 rounded-lg bg-[#7e55f6] text-white text-sm hover:bg-opacity-90 disabled:opacity-50 flex items-center gap-2 transition-colors"
                >
                  {emailBlastLoading && (
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  )}
                  Send Emails
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TutorsPage;